import { useEffect, useRef, useState } from 'react';

/**
 * Pull-to-refresh hook for any scroll container.
 *
 * Returns `{ ref, pulling, distance, refreshing }`:
 * - `ref` attaches to the scroll element. The container should set
 *   `overscroll-behavior: contain` so the page itself does not also bounce.
 * - `pulling` is true while the user is actively dragging down past the
 *   top of the container.
 * - `distance` is the current drag distance in px, clamped to `THRESHOLD * 1.5`.
 * - `refreshing` is true between `onRefresh()` invocation and its promise
 *   resolution; consumers render a spinner while it's set.
 *
 * Uses passive touch listeners on the host element to avoid janking the
 * native scroll. Only fires when the scroll position is already at the
 * very top — partway-scrolled lists keep their default behaviour.
 */
const THRESHOLD = 80;
const MAX_DRAG = THRESHOLD * 1.5;

export interface UsePullToRefreshOptions {
  onRefresh: () => void | Promise<unknown>;
  /** When false the hook becomes a no-op (e.g. while disabled or loading). */
  enabled?: boolean;
}

export interface UsePullToRefreshResult {
  ref: (el: HTMLElement | null) => void;
  pulling: boolean;
  distance: number;
  refreshing: boolean;
}

export function usePullToRefresh({
  onRefresh,
  enabled = true,
}: UsePullToRefreshOptions): UsePullToRefreshResult {
  const [pulling, setPulling] = useState(false);
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const elRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el || !enabled) return undefined;

    const onTouchStart = (e: TouchEvent) => {
      // Only arm if the user is already at the top of the container.
      if (el.scrollTop > 0) {
        startY.current = null;
        return;
      }
      startY.current = e.touches[0]?.clientY ?? null;
      setDistance(0);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startY.current === null) return;
      const y = e.touches[0]?.clientY ?? startY.current;
      const delta = y - startY.current;
      if (delta <= 0) {
        setPulling(false);
        setDistance(0);
        return;
      }
      // Apply some resistance so the pull feels rubbery rather than 1:1.
      const damped = Math.min(MAX_DRAG, delta * 0.55);
      setPulling(true);
      setDistance(damped);
    };

    const onTouchEnd = () => {
      if (startY.current === null) return;
      const dragged = distance;
      startY.current = null;
      setPulling(false);
      if (dragged >= THRESHOLD && !refreshing) {
        setRefreshing(true);
        Promise.resolve(onRefresh()).finally(() => {
          setRefreshing(false);
          setDistance(0);
        });
      } else {
        setDistance(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enabled, onRefresh, distance, refreshing]);

  const setRef = (el: HTMLElement | null) => {
    elRef.current = el;
  };

  return { ref: setRef, pulling, distance, refreshing };
}

export const PULL_TO_REFRESH_THRESHOLD = THRESHOLD;
